/**
 * Copy from referer-parser in npm
 * (https://www.npmjs.com/package/referer-parser)
 */
import { captureException } from '@jerry-serverless/next-sentry';

import refererConfig from './referer.json';

interface RefererDict {
  name: string;
  medium: string;
  params?: string[];
}

interface RefererConfigType {
  parameters?: string[];
  domains: string[];
}

const referers: Record<string, RefererDict> = loadReferers(refererConfig);

/**
 * Load refererConfig and deal into referer dict.
 */
function loadReferers(source: Record<string, Record<string, RefererConfigType>>): Record<string, RefererDict> {
  const referersDict: Record<string, RefererDict> = {};

  for (const medium in source) {
    const confList = source[medium];

    for (const refererName in confList) {
      const config = confList[refererName];
      let params: string[] | null = null;

      if (config.parameters) {
        params = config.parameters.map((p) => {
          return p.toLowerCase();
        });
      }

      config.domains.forEach((domain) => {
        referersDict[domain] = {
          name: refererName,
          medium,
        };
        if (params) {
          referersDict[domain]['params'] = params;
        }
      });
    }
  }
  return referersDict;
}

/**
 * Definition of Referer
 */
export default class Referer {
  known: boolean;
  referer: string | null;
  medium: string;
  searchParameter: string | null;
  searchTerm: string | null;
  referers: Record<string, RefererDict>;

  uri: URL;

  constructor(refererUrl: string, currentUrl?: string) {
    this.known = false;
    this.referer = null;
    this.medium = 'unknown';
    this.searchParameter = null;
    this.searchTerm = null;
    this.referers = referers;

    const refUri = new URL(refererUrl);
    const refHost = refUri.hostname;
    this.known = Boolean(~['http:', 'https:'].indexOf(refUri.protocol));
    this.uri = refUri;

    if (!this.known) return;

    if (currentUrl) {
      const currUri = new URL(currentUrl);
      const currHost = currUri.hostname;

      if (currHost === refHost) {
        this.medium = 'internal';
        return;
      }
    }

    let referer = this.lookupReferer(refHost, refUri.pathname, true);
    if (!referer) {
      referer = this.lookupReferer(refHost, refUri.pathname, false);
      if (!referer) {
        this.medium = 'unknown';
        return;
      }
    }

    this.referer = referer.name;
    this.medium = referer.medium;

    if (referer.medium === 'search') {
      if (!referer.params) return;
    }
  }

  /**
   * Lookup the corresponding referer in referer dict.
   */
  private lookupReferer(refHost: string, refPath: string, includePath: boolean): RefererDict | null {
    let referer: RefererDict | null = null;

    if (includePath) referer = this.referers[refHost + refPath];
    else referer = this.referers[refHost];

    if (!referer) {
      if (includePath) {
        const pathParts = refPath.split('/');
        if (pathParts.length > 1) {
          try {
            referer = this.referers[refHost + '/' + pathParts[1]];
          } catch (e) {
            captureException(e);
          }
        }
      }
    }

    if (!referer) {
      try {
        const idx = refHost.indexOf('.');
        if (idx === -1) return null;

        const slicedHost = refHost.slice(idx + 1);
        return this.lookupReferer(slicedHost, refPath, includePath);
      } catch (e) {
        captureException(e);
        return null;
      }
    } else return referer;
  }
}

// Example usage:
// const r = new Referer("http://www.google.com/search?q=gateway+oracle+cards+denise+linn&hl=en&client=safari")
// console.log(r.uri)
